<template>
  <div>
    <section class="section auth-ui-section--has-margin-top auth-ui-section--is-narrow">
      <div class="container auth-ui-container--has-centered-content">
        <v-card :classes="'auth-ui-card--is-not-full-width'">
          <div slot="header-title">
            {{ $t('auth.verifyEmailTitle') }}
          </div>
          <div slot="body">
            <article
              v-if="!serverResponse.visible"
            >
              <div>{{ $t('auth.verifyEmailDescription') }}</div>
            </article>
            <article
              v-else
              class="auth-ui-card__message-container"
            >
              <div
                :class="{'is-danger': serverResponse.error, 'is-success': !serverResponse.error }"
                class="message"
              >
                <div class="message-body">
                  <span>
                    {{ serverResponse.message }}
                    <a
                      v-if="getAfterVerifyUrl"
                      :href="getAfterVerifyUrl"
                    >{{ $t('auth.verifyEmailLinkText') }}.
                    </a>
                  </span>
                </div>
              </div>
            </article>
          </div>
        </v-card>
      </div>
    </section>
  </div>
</template>

<script>
/* ============
 * Verify Email Page
 * ============
 *
 * Page where a user verifies their email address after receiving a verification link via email.
 */

import VCard from '@/components/Card.vue';
import Request from '@/tools/Request';
import QueryStringTool from '@/tools/QueryStringTool';
import CookieTool from '@/tools/CookieTool';

export default {
  name: 'AuthVerifyEmail',

  components: {
    VCard,
  },

  data() {
    return {
      serverResponse: {
        message: '',
        visible: false,
        error: false,
      },
      key: QueryStringTool.getQueryStringParam('key'),
      email: QueryStringTool.getQueryStringParam('email'),
      afterVerifyUrl: '',
    };
  },

  computed: {
    isSignedIn() {
      let userInfo = CookieTool.getCookie('puheet_info');
      if (!userInfo) return false;
      userInfo = JSON.parse(decodeURIComponent(userInfo).slice(2)); // magic
      return userInfo.signedIn;
    },

    queryParamIsMissing() {
      return !this.key || !this.email;
    },

    getAfterVerifyUrl() {
      let url = false;

      const success = this.serverResponse.visible && !this.serverResponse.error;
      const error = this.serverResponse.visible && this.serverResponse.error;
      const hasTarget = !!this.afterVerifyUrl && this.afterVerifyUrl.length;

      if (success && this.isSignedIn) {
        url = process.env.VUE_APP_AFTER_SIGN_IN_URL;

        if (hasTarget) {
          url = this.afterVerifyUrl;
        }
      } else if (success && !this.isSignedIn) {
        url = `${window.location.origin}/auth/sign-in`;

        if (hasTarget) {
          url += `?target=${encodeURIComponent(this.afterVerifyUrl)}`;
        }
      } else if ((error && this.isSignedIn) || this.queryParamIsMissing) {
        url = `${window.location.origin}/request-verification-link`;
        if (hasTarget) {
          url += `?target=${encodeURIComponent(this.afterVerifyUrl)}`;
        }
      }

      return url; // returns false if error and not signed in
    },
  },

  mounted() {
    this.verifyEmail();
  },

  methods: {

    closeMessage() {
      this.serverResponse.visible = false;
    },

    showMessage(message = '', error = false) {
      this.serverResponse.visible = true;
      this.serverResponse.error = error;
      this.serverResponse.message = message;
    },

    async verifyEmail() {
      this.toggleShowSpinner(true);

      if (this.queryParamIsMissing) {
        this.showMessage(this.$t('auth.verifyEmailErrorMissingParams'), true);
        return;
      }

      const encodedEmail = encodeURIComponent(this.email);
      const url = `${process.env.VUE_APP_BACKEND_URL}/verify_email?email=${encodedEmail}&key=${this.key}`;

      try {
        const response = await Request.submit('GET', url, {});
        this.toggleShowSpinner(false);
        if (this.isSignedIn) {
          this.showMessage(this.$t('auth.verifyEmailSuccessSignedIn'));
        } else {
          this.showMessage(this.$t('auth.verifyEmailSuccessNotSignedIn'));
        }
        this.afterVerifyUrl = response.target;
      } catch (error) {
        this.toggleShowSpinner(false);
        if (this.isSignedIn) {
          this.showMessage(this.$t('auth.verifyEmailErrorSignedIn'), true);
        } else {
          this.showMessage(this.$t('auth.verifyEmailErrorNotSignedIn'), true);
        }
      }
    },

    /**
     * Toggles the app-wide spinner state in Vuex store.
     */
    toggleShowSpinner(isVisible = true) {
      this.$store.dispatch('general/setShowSpinner', isVisible);
    },
  },
};
</script>
